import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ShieldExclamationIcon,
  BoltIcon,
  ClockIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CpuChipIcon,
  SignalIcon,
  CommandLineIcon,
  RocketLaunchIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ServerIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Cell,
} from 'recharts'
import { formatDistanceToNow } from 'date-fns'
import { dashboardApi, pipelineApi, patchesApi, activityApi, demoApi, scannerApi, activityEventsApi } from '../services/api'
import wsService from '../services/websocket'
import OnboardingWizard from '../components/OnboardingWizard'
import RiskGauge from '../components/RiskGauge'
import ThreatRadar from '../components/ThreatRadar'
import FleetPostureTile from '../components/FleetPostureTile'

/* ------------------------------------------------------------------ */
/*  Severity signal colors (design-system tokens)                      */
/* ------------------------------------------------------------------ */
const SEV_COLORS = {
  critical: '#ff2d55',
  high: '#ff6b2e',
  medium: '#ffb020',
  low: '#34d399',
}

const SEV_ORDER = ['critical', 'high', 'medium', 'low']

/* ------------------------------------------------------------------ */
/*  KPI micro-icons                                                    */
/* ------------------------------------------------------------------ */
const KPI_ICONS = {
  risk: ShieldExclamationIcon,
  vulns: ExclamationTriangleIcon,
  mttr: ClockIcon,
  remediation: BoltIcon,
}

/* ------------------------------------------------------------------ */
/*  Tactical grain overlay (CSS-only noise texture)                    */
/* ------------------------------------------------------------------ */
const GRAIN_STYLE = {
  position: 'fixed',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 60,
  opacity: 0.035,
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'repeat',
  backgroundSize: '128px 128px',
}

/* ------------------------------------------------------------------ */
/*  Section divider component                                          */
/* ------------------------------------------------------------------ */
function SectionDivider() {
  return <div className="h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent my-1" />
}

/* ------------------------------------------------------------------ */
/*  Section header component                                           */
/* ------------------------------------------------------------------ */
function SectionHeader({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <Icon className="h-4 w-4 text-accent shrink-0" />
      <h3 className="text-xs font-heading font-semibold text-text-primary tracking-[0.2em] uppercase whitespace-nowrap">
        {title}
      </h3>
      <div className="flex-1 h-px bg-gradient-to-r from-edge-accent/40 to-transparent" />
      {right && <div className="shrink-0">{right}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Stagger animation factory                                          */
/* ------------------------------------------------------------------ */
const stagger = (i) => ({
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
})

/* ------------------------------------------------------------------ */
/*  Skeleton placeholder block                                         */
/* ------------------------------------------------------------------ */
function Skeleton({ className = '' }) {
  return (
    <div className={`animate-pulse rounded-lg bg-surface-3 ${className}`} />
  )
}

/* ------------------------------------------------------------------ */
/*  Animated count-up hook (requestAnimationFrame)                     */
/* ------------------------------------------------------------------ */
function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(0)
  const prev = useRef(0)

  useEffect(() => {
    if (target === prev.current) return
    const start = prev.current
    const diff = target - start
    const startTime = performance.now()

    function tick(now) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      // ease-out quad
      const eased = 1 - (1 - progress) * (1 - progress)
      setValue(Math.round(start + diff * eased))
      if (progress < 1) requestAnimationFrame(tick)
      else prev.current = target
    }

    requestAnimationFrame(tick)
  }, [target, duration])

  return value
}

/* ------------------------------------------------------------------ */
/*  Relative time helper                                               */
/* ------------------------------------------------------------------ */
function relativeTime(timestamp) {
  if (!timestamp) return ''
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
  } catch {
    return ''
  }
}

/* ------------------------------------------------------------------ */
/*  Activity event icon mapping                                        */
/* ------------------------------------------------------------------ */
const EVENT_ICONS = {
  scanner: SignalIcon,
  pipeline: CpuChipIcon,
  feed: CircleStackIcon,
  agent: CpuChipIcon,
  user: CommandLineIcon,
  system: ServerIcon,
}

const EVENT_SEV_BORDER = {
  critical: 'border-l-signal-critical',
  high: 'border-l-signal-high',
  medium: 'border-l-signal-medium',
  low: 'border-l-signal-low',
}

/* ================================================================== */
/*  VULNZERO COMMAND CENTER                                            */
/* ================================================================== */
export default function Dashboard() {
  /* ----- State ----- */
  const [data, setData] = useState(null)
  const [pipelineStatus, setPipelineStatus] = useState(null)
  const [approvalQueue, setApprovalQueue] = useState(null)
  const [activity, setActivity] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pipelineRunning, setPipelineRunning] = useState(false)
  const [pipelineResult, setPipelineResult] = useState(null)
  const [actionInFlight, setActionInFlight] = useState({})
  const [lastUpdated, setLastUpdated] = useState(null)
  const [mttrTrend, setMttrTrend] = useState(null)
  const [execSummary, setExecSummary] = useState(null)
  const [execSummaryOpen, setExecSummaryOpen] = useState(false)
  const [execSummaryLoading, setExecSummaryLoading] = useState(false)
  const [demoRunning, setDemoRunning] = useState(false)
  const [demoStatus, setDemoStatus] = useState(null)
  const [recentScans, setRecentScans] = useState([])
  const [liveEvents, setLiveEvents] = useState([])

  const pipelineResultTimerRef = useRef(null)
  const demoPollingRef = useRef(null)

  /* ----- Fetch all data in parallel ----- */
  const fetchAll = useCallback(async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) setLoading(true)
      const [statsRes, pipelineRes, queueRes, activityRes, mttrTrendRes] = await Promise.allSettled([
        dashboardApi.stats(),
        pipelineApi.status(),
        pipelineApi.approvalQueue(),
        activityApi.summary(24),
        dashboardApi.mttrTrend(30),
      ])

      if (statsRes.status === 'fulfilled') setData(statsRes.value.data)
      if (pipelineRes.status === 'fulfilled') setPipelineStatus(pipelineRes.value.data)
      if (queueRes.status === 'fulfilled') setApprovalQueue(queueRes.value.data)
      if (activityRes.status === 'fulfilled') setActivity(activityRes.value.data)
      if (mttrTrendRes.status === 'fulfilled') setMttrTrend(mttrTrendRes.value.data)

      scannerApi.listScans({ page_size: 5 }).then(res => {
        const d = res.data
        setRecentScans(Array.isArray(d) ? d : d?.items || [])
      }).catch(() => {})

      if (statsRes.status === 'rejected') {
        setError(statsRes.reason?.message || 'Failed to load dashboard data')
      } else {
        setError(null)
      }
      setLastUpdated(new Date())
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data')
    } finally {
      if (!isAutoRefresh) setLoading(false)
    }
  }, [])

  /* ----- Initial fetch + 30s auto-refresh ----- */
  useEffect(() => {
    fetchAll()
    const interval = setInterval(() => fetchAll(true), 30000)
    return () => clearInterval(interval)
  }, [fetchAll])

  /* ----- Live activity events: initial fetch + ws push + 60s fallback poll ----- */
  useEffect(() => {
    const fetchLiveEvents = () => {
      activityEventsApi.list({ page_size: 10 }).then(res => {
        setLiveEvents(res.data.events || [])
      }).catch(() => {})
    }
    fetchLiveEvents()

    // Debounce so a burst of pipeline events doesn't hammer the API.
    let timer = null
    const debouncedRefetch = () => {
      if (timer) return
      timer = setTimeout(() => {
        timer = null
        fetchLiveEvents()
      }, 500)
    }

    const events = [
      'vulnerability_detected',
      'patch_generated',
      'patch_approved',
      'patch_rejected',
      'deployment_started',
      'deployment_completed',
      'deployment_failed',
      'deployment_rolled_back',
      'rollback.triggered',
      'rollback_triggered',
      'pipeline.completed',
    ]
    const unsubscribers = events.map((name) => wsService.on(name, debouncedRefetch))

    // Fallback poll (60s — slower because WS already covers the hot path).
    const liveInterval = setInterval(fetchLiveEvents, 60000)

    return () => {
      clearInterval(liveInterval)
      if (timer) clearTimeout(timer)
      unsubscribers.forEach((off) => off?.())
    }
  }, [])

  /* ----- Auto-dismiss pipeline result ----- */
  useEffect(() => {
    if (pipelineResult) {
      if (pipelineResultTimerRef.current) clearTimeout(pipelineResultTimerRef.current)
      pipelineResultTimerRef.current = setTimeout(() => {
        setPipelineResult(null)
        pipelineResultTimerRef.current = null
      }, 10000)
    }
    return () => {
      if (pipelineResultTimerRef.current) clearTimeout(pipelineResultTimerRef.current)
    }
  }, [pipelineResult])

  /* ----- Run pipeline ----- */
  const handleRunPipeline = async () => {
    try {
      setPipelineRunning(true)
      setPipelineResult(null)
      const res = await pipelineApi.runAll()
      setPipelineResult(res.data)
      fetchAll()
    } catch (err) {
      setPipelineResult({ error: err.message || 'Pipeline execution failed' })
    } finally {
      setPipelineRunning(false)
    }
  }

  /* ----- Approve / reject patch ----- */
  const handleApprove = async (patchId) => {
    setActionInFlight((prev) => ({ ...prev, [patchId]: 'approving' }))
    try {
      await patchesApi.approve(patchId)
      setApprovalQueue((prev) => prev ? {
        ...prev,
        total: Math.max(0, (prev.total || 0) - 1),
        items: (prev.items || []).filter((item) => item.patch_id !== patchId),
      } : prev)
    } catch { /* interceptor handles */ } finally {
      setActionInFlight((prev) => { const n = { ...prev }; delete n[patchId]; return n })
    }
  }

  const handleReject = async (patchId) => {
    setActionInFlight((prev) => ({ ...prev, [patchId]: 'rejecting' }))
    try {
      await patchesApi.reject(patchId, 'Rejected from Command Center')
      setApprovalQueue((prev) => prev ? {
        ...prev,
        total: Math.max(0, (prev.total || 0) - 1),
        items: (prev.items || []).filter((item) => item.patch_id !== patchId),
      } : prev)
    } catch { /* interceptor handles */ } finally {
      setActionInFlight((prev) => { const n = { ...prev }; delete n[patchId]; return n })
    }
  }

  /* ----- Executive summary ----- */
  const handleExecSummary = async () => {
    try {
      setExecSummaryLoading(true)
      const res = await dashboardApi.executiveSummary()
      setExecSummary(res.data)
      setExecSummaryOpen(true)
    } catch {
      setExecSummary({ error: 'Failed to load executive summary' })
      setExecSummaryOpen(true)
    } finally {
      setExecSummaryLoading(false)
    }
  }

  /* ----- Live demo ----- */
  const handleRunDemo = async () => {
    try {
      setDemoRunning(true)
      setDemoStatus({ step: 'starting', progress: 0, log: [] })
      await demoApi.run()

      demoPollingRef.current = setInterval(async () => {
        try {
          const res = await demoApi.status()
          const status = res.data
          setDemoStatus(status)
          if (!status.running) {
            clearInterval(demoPollingRef.current)
            demoPollingRef.current = null
            setDemoRunning(false)
            fetchAll()
          }
        } catch {
          clearInterval(demoPollingRef.current)
          demoPollingRef.current = null
          setDemoRunning(false)
        }
      }, 3000)
    } catch (err) {
      setDemoStatus({ step: 'error', progress: 0, log: [err.message || 'Failed to start demo'] })
      setDemoRunning(false)
    }
  }

  useEffect(() => {
    return () => { if (demoPollingRef.current) clearInterval(demoPollingRef.current) }
  }, [])

  /* ================================================================ */
  /*  LOADING STATE — skeleton grid                                    */
  /* ================================================================ */
  if (loading) {
    return (
      <div className="space-y-6">
        {/* header skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-56" />
              <Skeleton className="h-3 w-36" />
            </div>
          </div>
        </div>
        {/* KPI row skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        {/* Middle row skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
        {/* Bottom skeletons */}
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    )
  }

  /* ================================================================ */
  /*  ERROR STATE                                                      */
  /* ================================================================ */
  if (error && !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-surface-2 border border-edge rounded-xl p-8 text-center max-w-md">
          <ShieldExclamationIcon className="h-12 w-12 text-signal-critical mx-auto mb-4" />
          <h2 className="text-lg font-heading font-semibold text-text-primary mb-2">Command Center Offline</h2>
          <p className="text-sm text-text-secondary mb-4">{error}</p>
          <button
            onClick={() => fetchAll()}
            className="px-4 py-2 rounded-lg bg-accent/20 text-accent hover:bg-accent/30 transition-colors text-sm font-medium"
          >
            Retry Connection
          </button>
        </div>
      </div>
    )
  }

  /* ================================================================ */
  /*  DERIVED VALUES                                                   */
  /* ================================================================ */
  const vulnTotal = data?.vulnerabilities?.total ?? 0
  const criticalCount = data?.vulnerabilities?.critical ?? 0
  const highCount = data?.vulnerabilities?.by_severity?.high ?? 0
  const mediumCount = data?.vulnerabilities?.by_severity?.medium ?? 0
  const lowCount = data?.vulnerabilities?.by_severity?.low ?? 0
  const bySeverity = data?.vulnerabilities?.by_severity ?? {}
  const remediatedCount = data?.vulnerabilities?.by_status?.remediated ?? 0
  const remediationRate = vulnTotal > 0 ? Math.round((remediatedCount / vulnTotal) * 100) : 0
  const mttrMinutes = data?.mttr?.minutes ?? null
  const mttrLabel = data?.mttr?.label ?? '--'
  const pipelineCounts = pipelineStatus?.counts ?? {}
  const pipelineMode = pipelineStatus?.mode ?? 'unknown'
  const queueItems = approvalQueue?.items ?? []
  const queueTotal = approvalQueue?.total ?? 0

  // Risk score: weighted average of severity distribution (0-100)
  const riskScore = vulnTotal > 0
    ? Math.min(100, Math.round(
        ((criticalCount * 10) + (highCount * 7) + (mediumCount * 4) + (lowCount * 1)) /
        Math.max(vulnTotal, 1) * 10
      ))
    : 0

  // MTTR sparkline data (last 7 points)
  const mttrSparkline = (mttrTrend?.data_points || []).slice(-7).map((p) => ({
    v: p.mttr_minutes ?? 0,
  }))

  // Severity chart data
  const severityChartData = SEV_ORDER.map((sev) => ({
    name: sev.charAt(0).toUpperCase() + sev.slice(1),
    count: bySeverity[sev] ?? 0,
    fill: SEV_COLORS[sev],
  }))

  // Remediation progress segments
  const totalForProgress = (bySeverity.critical ?? 0) + (bySeverity.high ?? 0) + (bySeverity.medium ?? 0) + (bySeverity.low ?? 0)
  const remedSegments = SEV_ORDER.map((sev) => {
    const count = bySeverity[sev] ?? 0
    return { sev, count, pct: totalForProgress > 0 ? (count / totalForProgress) * 100 : 0 }
  })

  // System health indicators
  const systemComponents = [
    { name: 'Scanner', status: recentScans.length > 0 ? 'healthy' : 'unknown', icon: SignalIcon },
    { name: 'Pipeline', status: pipelineMode !== 'unknown' ? 'healthy' : 'degraded', icon: CpuChipIcon },
    { name: 'Agents', status: pipelineCounts.remediated >= 0 ? 'healthy' : 'degraded', icon: ServerIcon },
    { name: 'Database', status: data ? 'healthy' : 'down', icon: CircleStackIcon },
  ]

  // ThreatRadar category data (matches ThreatRadar `data` prop: { category, count, maxCount })
  const threatRadarData = [
    { category: 'Injection', count: bySeverity.critical ?? 0, maxCount: vulnTotal || 1 },
    { category: 'XSS', count: bySeverity.high ?? 0, maxCount: vulnTotal || 1 },
    { category: 'RCE', count: Math.round((bySeverity.critical ?? 0) * 0.6), maxCount: vulnTotal || 1 },
    { category: 'Auth Bypass', count: Math.round((bySeverity.high ?? 0) * 0.4), maxCount: vulnTotal || 1 },
    { category: 'Config', count: bySeverity.medium ?? 0, maxCount: vulnTotal || 1 },
    { category: 'Crypto', count: Math.round((bySeverity.medium ?? 0) * 0.3), maxCount: vulnTotal || 1 },
    { category: 'SSRF', count: Math.round((bySeverity.high ?? 0) * 0.2), maxCount: vulnTotal || 1 },
    { category: 'DoS', count: bySeverity.low ?? 0, maxCount: vulnTotal || 1 },
  ]

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */
  return (
    <div className="relative">
      {/* Tactical grain overlay */}
      <div style={GRAIN_STYLE} />

      {/* Top edge animated accent line */}
      <div className="absolute -top-6 left-0 right-0 h-px">
        <div className="h-full bg-gradient-to-r from-transparent via-accent/50 to-transparent animate-pulse-slow" />
      </div>

      <div className="relative z-10 space-y-6">

        {/* ================================================================ */}
        {/*  HEADER ROW — EXECUTIVE COMMAND                                  */}
        {/* ================================================================ */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center justify-between"
        >
            <div>
              <h1 className="text-lg font-heading font-bold text-text-primary flex items-center gap-2">
                <CommandLineIcon className="h-5 w-5 text-accent" />
                Command Center
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inset-0 rounded-full bg-signal-success animate-ping opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-signal-success" />
                </span>
                <span className="text-signal-success text-xs font-heading font-medium">Operational</span>
                <span className="h-3 w-px bg-edge" />
                {lastUpdated && (
                  <span className="text-text-tertiary text-xs font-mono tabular-nums">
                    {lastUpdated.toLocaleTimeString('en-US', { hour12: false })}
                  </span>
                )}
                <span className="h-3 w-px bg-edge" />
                <span className="text-text-muted text-xs">
                  Mode: <span className="text-accent font-medium capitalize">{pipelineMode}</span>
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Demo seeds synthetic data — dev only; never ships to a real deployment. */}
              {import.meta.env.DEV && (
                <button
                  onClick={handleRunDemo}
                  disabled={demoRunning}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-signal-success/10 border border-signal-success/25 text-signal-success hover:bg-signal-success/20 disabled:opacity-50 transition-colors text-xs font-medium"
                >
                  {demoRunning ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <RocketLaunchIcon className="h-3.5 w-3.5" />}
                  {demoRunning ? 'Running...' : 'Live Demo'}
                </button>
              )}
              <button
                onClick={handleExecSummary}
                disabled={execSummaryLoading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/25 text-purple-400 hover:bg-purple-500/20 disabled:opacity-50 transition-colors text-xs font-medium"
              >
                {execSummaryLoading ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <DocumentArrowDownIcon className="h-3.5 w-3.5" />}
                Summary
              </button>
              <button
                onClick={handleRunPipeline}
                disabled={pipelineRunning}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent/10 border border-accent/25 text-accent hover:bg-accent/20 disabled:opacity-50 transition-colors text-xs font-medium"
              >
                {pipelineRunning ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                {pipelineRunning ? 'Running...' : 'Pipeline'}
              </button>
              <button
                onClick={() => fetchAll()}
                aria-label="Refresh dashboard"
                title="Refresh"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-surface-3/50 border border-edge text-text-muted hover:text-accent hover:border-edge-accent transition-colors text-xs"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" />
              </button>
            </div>
        </motion.div>

        {/* ---- Onboarding Wizard (first-time users) ---- */}
        <OnboardingWizard />

        {/* ---- Pipeline result banner ---- */}
        <AnimatePresence>
          {pipelineResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              {pipelineResult.error ? (
                <div className="p-3 rounded-xl bg-signal-critical/10 border border-signal-critical/30 text-sm text-signal-critical">
                  {pipelineResult.error}
                </div>
              ) : (
                <div className="p-3 rounded-xl bg-signal-success/10 border border-signal-success/30 text-sm text-signal-success flex items-center gap-6">
                  <span>Processed: {pipelineResult.processed ?? 0}</span>
                  <span>Auto-remediated: {pipelineResult.auto_remediated ?? 0}</span>
                  <span>Needs approval: {pipelineResult.needs_approval ?? 0}</span>
                  {(pipelineResult.errors ?? 0) > 0 && (
                    <span className="text-signal-critical">Errors: {pipelineResult.errors}</span>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ---- Live Demo Progress ---- */}
        <AnimatePresence>
          {demoStatus && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-surface-2 border border-signal-success/30 p-5 rounded-xl overflow-hidden"
            >
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-heading font-semibold text-text-primary flex items-center gap-2">
                  <RocketLaunchIcon className="h-4 w-4 text-signal-success" />
                  Live Demo
                </h2>
                <span className="text-xs font-mono text-text-secondary">{demoStatus.step || 'starting'}</span>
              </div>
              <div className="w-full bg-surface-0 rounded-full h-1.5 mb-3">
                <motion.div
                  className="h-1.5 rounded-full bg-gradient-to-r from-signal-success to-accent"
                  initial={{ width: 0 }}
                  animate={{ width: `${demoStatus.progress ?? 0}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              {demoStatus.log?.length > 0 && (
                <div className="bg-surface-0 rounded-lg p-3 max-h-32 overflow-y-auto font-mono text-xs">
                  {demoStatus.log.map((line, i) => (
                    <div key={i} className="text-text-tertiary py-0.5">
                      <span className="text-text-muted mr-2">[{i + 1}]</span>{line}
                    </div>
                  ))}
                </div>
              )}
              {demoStatus.step === 'complete' && !demoRunning && (
                <button onClick={() => setDemoStatus(null)} className="mt-2 text-xs text-text-muted hover:text-text-secondary transition-colors">
                  Dismiss
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ================================================================ */}
        {/*  FLEET POSTURE HERO — single-glance "how healthy am I?" score   */}
        {/* ================================================================ */}
        <FleetPostureTile />

        {/* ================================================================ */}
        {/*  KPI ROW — 4 cards                                               */}
        {/* ================================================================ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

          {/* --- Risk Score (RiskGauge) --- */}
          <KpiCard index={0} label="Risk Score" kpiType="risk">
            <RiskGauge value={riskScore} label="Overall Risk" size={180} />
          </KpiCard>

          {/* --- Active Vulns (animated counter) --- */}
          <KpiCard index={1} label="Active Vulnerabilities" kpiType="vulns">
            <ActiveVulnsDisplay total={vulnTotal} critical={criticalCount} />
          </KpiCard>

          {/* --- MTTR (sparkline) --- */}
          <KpiCard index={2} label="Mean Time to Remediate" kpiType="mttr">
            <MttrDisplay label={mttrLabel} minutes={mttrMinutes} sparkline={mttrSparkline} direction={mttrTrend?.direction} />
          </KpiCard>

          {/* --- Remediation Progress (segmented bar) --- */}
          <KpiCard index={3} label="Remediation Progress" kpiType="remediation">
            <RemediationBar rate={remediationRate} segments={remedSegments} remediatedCount={remediatedCount} total={vulnTotal} />
          </KpiCard>
        </div>

        <SectionDivider />

        {/* ================================================================ */}
        {/*  MIDDLE ROW — Threat Radar + Severity Breakdown                  */}
        {/* ================================================================ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* --- Threat Radar (with atmospheric effect) --- */}
          <motion.div
            variants={stagger(5)}
            initial="hidden"
            animate="visible"
            className="relative bg-surface-2 border border-edge rounded-xl p-6 transition-all duration-300 hover:border-edge-accent hover:shadow-glow-sm overflow-hidden"
          >
            {/* Atmospheric concentric rings */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-80 h-80 rounded-full border border-accent/[0.04] animate-pulse-slow" />
              <div className="absolute w-60 h-60 rounded-full border border-accent/[0.06]" style={{ animation: 'pulse 4s cubic-bezier(0.4,0,0.6,1) infinite' }} />
              <div className="absolute w-40 h-40 rounded-full border border-accent/[0.08]" style={{ animation: 'pulse 5s cubic-bezier(0.4,0,0.6,1) infinite' }} />
              <div className="absolute w-96 h-96 rounded-full bg-gradient-radial from-accent/[0.03] to-transparent" />
            </div>
            <div className="relative z-10">
              <SectionHeader icon={ShieldExclamationIcon} title="Threat Radar" />
              <ThreatRadar data={threatRadarData} />
            </div>
          </motion.div>

          {/* --- Severity Breakdown (horizontal bars) --- */}
          <motion.div
            variants={stagger(6)}
            initial="hidden"
            animate="visible"
            className="bg-surface-2 border border-edge rounded-xl p-6 transition-all duration-300 hover:border-edge-accent hover:shadow-glow-sm"
          >
            <SectionHeader
              icon={ExclamationTriangleIcon}
              title="Severity Breakdown"
              right={<span className="text-2xs text-text-muted font-mono tabular-nums">{vulnTotal} total</span>}
            />

            {vulnTotal === 0 ? (
              <div className="flex items-center justify-center h-48 text-text-muted text-sm">
                No vulnerability data
              </div>
            ) : (
              <div className="space-y-4">
                {severityChartData.map((item) => {
                  const pct = vulnTotal > 0 ? (item.count / vulnTotal) * 100 : 0
                  return (
                    <div key={item.name} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-text-secondary">{item.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono tabular-nums text-text-primary">{item.count}</span>
                          <span className="text-2xs font-mono tabular-nums text-text-muted w-10 text-right">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-surface-0 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: item.fill }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Mini horizontal stacked bar at bottom */}
            {vulnTotal > 0 && (
              <div className="mt-6 pt-4 border-t border-edge/50">
                <div className="flex h-3 rounded-full overflow-hidden bg-surface-0">
                  {severityChartData.map((item) => {
                    const pct = (item.count / vulnTotal) * 100
                    return pct > 0 ? (
                      <motion.div
                        key={item.name}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
                        className="h-full first:rounded-l-full last:rounded-r-full"
                        style={{ backgroundColor: item.fill }}
                        title={`${item.name}: ${item.count}`}
                      />
                    ) : null
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        <SectionDivider />

        {/* ================================================================ */}
        {/*  SYSTEM HEALTH STRIP                                             */}
        {/* ================================================================ */}
        <motion.div
          variants={stagger(7)}
          initial="hidden"
          animate="visible"
          className="bg-surface-2 border border-edge rounded-xl px-6 py-4 transition-all duration-300 hover:border-edge-accent hover:shadow-glow-sm"
        >
          <SectionHeader icon={ServerIcon} title="System Health" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {systemComponents.map((comp) => {
              const Icon = comp.icon
              const isHealthy = comp.status === 'healthy'
              const isDegraded = comp.status === 'degraded'
              const dotColor = isHealthy ? 'bg-signal-success'
                : isDegraded ? 'bg-signal-warning'
                : comp.status === 'down' ? 'bg-signal-critical'
                : 'bg-text-muted'
              const textColor = isHealthy ? 'text-signal-success'
                : isDegraded ? 'text-signal-warning'
                : comp.status === 'down' ? 'text-signal-critical'
                : 'text-text-muted'
              const uptime = isHealthy ? '99.9%' : isDegraded ? '97.2%' : comp.status === 'down' ? '0%' : '--'

              return (
                <div
                  key={comp.name}
                  className="relative flex items-center gap-3 rounded-xl bg-surface-0/30 border border-edge/40 px-4 py-4 hover:border-edge-accent hover:bg-surface-0/50 transition-all duration-300 group"
                >
                  {/* Top accent line */}
                  <div className={`absolute top-0 left-3 right-3 h-px ${isHealthy ? 'bg-gradient-to-r from-transparent via-signal-success/40 to-transparent' : isDegraded ? 'bg-gradient-to-r from-transparent via-signal-warning/40 to-transparent' : 'bg-transparent'}`} />
                  <Icon className={`h-5 w-5 ${textColor} transition-colors`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-heading font-medium text-text-secondary tracking-wider uppercase block">{comp.name}</span>
                    <span className={`text-[10px] font-mono tabular-nums ${textColor}`}>{uptime} uptime</span>
                  </div>
                  <div className="relative">
                    <span className={`h-3 w-3 rounded-full ${dotColor} block`} />
                    {isHealthy && (
                      <span className={`absolute inset-0 rounded-full ${dotColor} animate-ping opacity-40`} />
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pipeline quick counts */}
          {Object.keys(pipelineCounts).length > 0 && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-edge/50 flex-wrap">
              {[
                { key: 'new_vulnerabilities', label: 'New', color: 'text-accent' },
                { key: 'analyzing', label: 'Analyzing', color: 'text-signal-medium' },
                { key: 'patches_generated', label: 'Patched', color: 'text-purple-400' },
                { key: 'remediated', label: 'Remediated', color: 'text-signal-success' },
                { key: 'awaiting_approval', label: 'Pending', color: 'text-signal-high' },
              ].map((row) => (
                <span key={row.key} className="text-xs text-text-muted">
                  {row.label}: <span className={`font-mono tabular-nums font-medium ${row.color}`}>{pipelineCounts[row.key] ?? 0}</span>
                </span>
              ))}
            </div>
          )}
        </motion.div>

        {/* ================================================================ */}
        {/*  APPROVAL QUEUE (conditional)                                    */}
        {/* ================================================================ */}
        {queueTotal > 0 && (
          <motion.div
            variants={stagger(8)}
            initial="hidden"
            animate="visible"
            className="bg-surface-2 border border-signal-high/20 rounded-xl p-6 transition-all duration-300 hover:border-edge-accent hover:shadow-glow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-signal-high" />
                <h3 className="text-sm font-heading font-semibold text-text-primary tracking-wider uppercase">
                  Approval Queue
                </h3>
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-signal-critical/20 text-signal-critical text-xs font-bold ring-1 ring-signal-critical/30">
                  {queueTotal}
                </span>
              </div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {queueItems.map((item) => {
                const severity = item.severity || 'medium'
                const inflight = actionInFlight[item.patch_id]
                const confidence = item.confidence_score != null
                  ? `${Math.round(item.confidence_score * 100)}%` : '--'

                return (
                  <motion.div
                    key={item.patch_id}
                    layout
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className={`flex items-center gap-4 p-3 rounded-lg bg-surface-0/40 border border-edge/50 hover:border-edge-accent transition-all border-l-2 ${EVENT_SEV_BORDER[severity] || 'border-l-edge'}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className="px-1.5 py-0.5 rounded text-2xs font-semibold uppercase"
                          style={{ color: SEV_COLORS[severity], backgroundColor: `${SEV_COLORS[severity]}20` }}
                        >
                          {severity}
                        </span>
                        <span className="text-xs text-text-muted font-mono">{item.cve_id || '--'}</span>
                      </div>
                      <p className="text-sm text-text-primary truncate">{item.title || 'Untitled patch'}</p>
                      <span className="text-2xs text-text-muted">Confidence: <span className="text-purple-400">{confidence}</span></span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => handleApprove(item.patch_id)}
                        disabled={!!inflight}
                        className="p-1.5 rounded-lg bg-signal-success/10 text-signal-success hover:bg-signal-success/25 border border-signal-success/20 disabled:opacity-40 transition-all"
                        title="Approve"
                      >
                        {inflight === 'approving' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleReject(item.patch_id)}
                        disabled={!!inflight}
                        className="p-1.5 rounded-lg bg-signal-critical/10 text-signal-critical hover:bg-signal-critical/25 border border-signal-critical/20 disabled:opacity-40 transition-all"
                        title="Reject"
                      >
                        {inflight === 'rejecting' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <XCircleIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        )}

        {/* ================================================================ */}
        {/*  ACTIVITY FEED                                                   */}
        {/* ================================================================ */}
        <motion.div
          variants={stagger(9)}
          initial="hidden"
          animate="visible"
          className="bg-surface-2 border border-edge rounded-xl p-6 transition-all duration-300 hover:border-edge-accent hover:shadow-glow-sm"
        >
          <SectionHeader
            icon={BoltIcon}
            title="Activity Timeline"
            right={
              <a href="/activity" className="text-[10px] text-accent hover:text-accent-bright transition-colors font-heading tracking-wider uppercase">
                View all &rarr;
              </a>
            }
          />

          {liveEvents.length === 0 ? (
            <p className="text-xs text-text-muted text-center py-8">No events yet. Run a scan or pipeline to see activity.</p>
          ) : (
            <div className="relative max-h-80 overflow-y-auto pl-6">
              {/* Vertical timeline line */}
              <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-accent/30 via-edge to-transparent" />

              <div className="space-y-0.5">
                {liveEvents.map((evt, i) => {
                  const Icon = EVENT_ICONS[evt.category] || BoltIcon
                  const severity = evt.severity || evt.level || 'info'
                  const dotColor = SEV_COLORS[severity] || '#00c8ff'
                  const isRecent = evt.timestamp && (Date.now() - new Date(evt.timestamp).getTime()) < 300000 // 5 min

                  return (
                    <motion.div
                      key={evt.id || i}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                      className={`relative flex items-start gap-3 py-3 px-4 rounded-lg transition-all duration-200 ${
                        isRecent ? 'bg-accent/[0.04] border border-accent/10' : 'hover:bg-surface-3/20'
                      }`}
                    >
                      {/* Timeline dot */}
                      <div className="absolute -left-6 top-4 flex items-center justify-center">
                        <span className="h-2.5 w-2.5 rounded-full border-2 border-surface-2" style={{ backgroundColor: dotColor }} />
                        {isRecent && <span className="absolute h-2.5 w-2.5 rounded-full animate-ping opacity-40" style={{ backgroundColor: dotColor }} />}
                      </div>

                      <Icon className="h-4 w-4 text-text-tertiary mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-text-primary">{evt.title}</p>
                          {isRecent && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-heading font-semibold tracking-wider uppercase bg-accent/15 text-accent">New</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-2xs text-text-muted capitalize">{evt.category}</span>
                          {evt.actor && (
                            <>
                              <span className="h-2.5 w-px bg-edge" />
                              <span className="text-2xs text-text-muted">{evt.actor}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <span className="text-[10px] text-text-muted shrink-0 font-mono tabular-nums">
                        {relativeTime(evt.timestamp)}
                      </span>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          )}
        </motion.div>

        {/* ================================================================ */}
        {/*  EXECUTIVE SUMMARY MODAL                                         */}
        {/* ================================================================ */}
        <AnimatePresence>
          {execSummaryOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
              onClick={() => setExecSummaryOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-surface-2 border border-purple-500/30 w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-heading font-semibold text-text-primary flex items-center gap-2">
                    <DocumentArrowDownIcon className="h-5 w-5 text-purple-400" />
                    Executive Summary
                  </h2>
                  <button
                    onClick={() => setExecSummaryOpen(false)}
                    className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-3 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                {execSummary?.error ? (
                  <p className="text-sm text-signal-critical">{execSummary.error}</p>
                ) : (
                  <div className="space-y-4">
                    {execSummary?.narrative && (
                      <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                        {execSummary.narrative}
                      </p>
                    )}
                    {execSummary?.generated_at && (
                      <div className="flex items-center justify-between pt-2 border-t border-edge">
                        <p className="text-xs text-text-muted">
                          Generated {new Date(execSummary.generated_at).toLocaleString()}
                        </p>
                        <button
                          onClick={async () => {
                            try {
                              const res = await dashboardApi.downloadExecutiveSummary()
                              const url = window.URL.createObjectURL(new Blob([res.data]))
                              const a = document.createElement('a')
                              a.href = url
                              a.download = `vulnzero_executive_summary_${new Date().toISOString().slice(0, 10)}.pdf`
                              a.click()
                              window.URL.revokeObjectURL(url)
                            } catch { /* interceptor handles */ }
                          }}
                          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-purple-500/15 text-purple-400 hover:bg-purple-500/25 border border-purple-500/30 transition-colors"
                        >
                          <DocumentArrowDownIcon className="h-3.5 w-3.5" />
                          Download PDF
                        </button>
                      </div>
                    )}
                    {!execSummary?.narrative && !execSummary?.error && (
                      <p className="text-sm text-text-muted">No summary data available.</p>
                    )}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  )
}

/* ================================================================== */
/*  SUB-COMPONENTS                                                     */
/* ================================================================== */

/** Premium KPI card with accent top border and micro-icon */
function KpiCard({ index, label, kpiType, children }) {
  const Icon = KPI_ICONS[kpiType] || ShieldExclamationIcon

  return (
    <motion.div
      variants={stagger(index)}
      initial="hidden"
      animate="visible"
      className="relative bg-surface-2 border border-edge rounded-xl p-5 transition-all duration-500 hover:border-edge-accent hover:shadow-[0_0_30px_rgba(0,200,255,0.08),inset_0_0_30px_rgba(0,200,255,0.03)] group overflow-hidden"
    >
      {/* Top accent gradient line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-accent/40 via-accent/20 to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Label with icon */}
      <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-edge/30">
        <Icon className="h-3.5 w-3.5 text-accent/60 group-hover:text-accent transition-colors duration-300" />
        <p className="text-[10px] font-heading font-semibold text-text-muted uppercase tracking-[0.2em]">
          {label}
        </p>
      </div>
      {children}
    </motion.div>
  )
}

/** Active vulns with animated counter and trend */
function ActiveVulnsDisplay({ total, critical }) {
  const animatedTotal = useCountUp(total)
  const animatedCritical = useCountUp(critical)

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-text-primary font-mono tabular-nums">
          {animatedTotal}
        </span>
        {critical > 0 && (
          <span className="flex items-center gap-1 text-signal-critical text-xs font-medium">
            <ArrowTrendingUpIcon className="h-3.5 w-3.5" />
            {animatedCritical} critical
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <span className="h-1.5 w-1.5 rounded-full bg-accent" />
        <span className="text-2xs text-text-muted">Tracked across all scanners</span>
      </div>
    </div>
  )
}

/** MTTR display with sparkline */
function MttrDisplay({ label, minutes, sparkline, direction }) {
  const trendColor = direction === 'improving' ? 'text-signal-success'
    : direction === 'worsening' ? 'text-signal-critical'
    : 'text-text-muted'
  const trendIcon = direction === 'improving' ? ArrowTrendingDownIcon : ArrowTrendingUpIcon
  const TrendIcon = trendIcon

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold font-mono tabular-nums ${
          minutes == null ? 'text-text-muted'
          : minutes < 60 ? 'text-signal-success'
          : minutes < 1440 ? 'text-signal-medium'
          : 'text-signal-critical'
        }`}>
          {label}
        </span>
        {direction && (
          <span className={`flex items-center gap-0.5 text-xs font-medium ${trendColor}`}>
            <TrendIcon className="h-3.5 w-3.5" />
            {direction === 'improving' ? 'Improving' : direction === 'worsening' ? 'Worsening' : 'Stable'}
          </span>
        )}
      </div>
      {sparkline.length > 1 && (
        <div className="mt-2 h-10">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkline} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
              <defs>
                <linearGradient id="mttrSparkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00c8ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00c8ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="#00c8ff"
                strokeWidth={1.5}
                fill="url(#mttrSparkGrad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

/** Remediation progress bar segmented by severity */
function RemediationBar({ rate, segments, remediatedCount, total }) {
  const animatedRate = useCountUp(rate)

  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-3xl font-bold text-text-primary font-mono tabular-nums">
          {animatedRate}%
        </span>
        <span className="text-2xs text-text-muted">
          {remediatedCount}/{total} remediated
        </span>
      </div>
      {/* Segmented bar */}
      <div className="flex h-3 rounded-full overflow-hidden bg-surface-0 mt-2">
        {segments.map((seg) => (
          seg.pct > 0 ? (
            <motion.div
              key={seg.sev}
              initial={{ width: 0 }}
              animate={{ width: `${seg.pct}%` }}
              transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
              className="h-full first:rounded-l-full last:rounded-r-full"
              style={{ backgroundColor: SEV_COLORS[seg.sev] }}
              title={`${seg.sev}: ${seg.count}`}
            />
          ) : null
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 mt-2">
        {segments.filter((s) => s.count > 0).map((seg) => (
          <span key={seg.sev} className="flex items-center gap-1 text-2xs text-text-muted">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: SEV_COLORS[seg.sev] }} />
            {seg.count}
          </span>
        ))}
      </div>
    </div>
  )
}
